import React, { useState, useEffect, useRef, useCallback } from 'react';
import './HeroCarousel.css';
import slide1Desktop from '../assets/images/slider-1.jpeg';
import slide2Desktop from '../assets/images/slider-2.jpeg';
import slide1Mobile from '../assets/images/mobileview2.png';
import slide2Mobile from '../assets/images/mobileview.png';

const slides = [
  { id: 1, desktop: slide1Desktop, mobile: slide1Mobile, alt: 'Banner 1' },
  { id: 2, desktop: slide2Desktop, mobile: slide2Mobile, alt: 'Banner 2' },
];

const HeroCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [animDir, setAnimDir] = useState('next');
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);
  const total = slides.length;

  const goTo = useCallback(
    (index, dir = 'next') => {
      if (transitioning) return;
      setAnimDir(dir);
      setTransitioning(true);
      setTimeout(() => {
        setCurrent(index);
        setTransitioning(false);
      }, 420);
    },
    [transitioning]
  );

  const next = useCallback(
    () => goTo((current + 1) % total, 'next'),
    [current, total, goTo]
  );
  const prev = useCallback(
    () => goTo((current - 1 + total) % total, 'prev'),
    [current, total, goTo]
  );

  const resetTimer = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(next, 4500);
  }, [next]);

  useEffect(() => {
    intervalRef.current = setInterval(next, 4500);
    return () => clearInterval(intervalRef.current);
  }, [next]);

  const handlePrev = () => { prev(); resetTimer(); };
  const handleNext = () => { next(); resetTimer(); };
  const handleDot = (i) => {
    if (i !== current) {
      goTo(i, i > current ? 'next' : 'prev');
      resetTimer();
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? handleNext() : handlePrev();
    }
    touchStartX.current = null;
  };

  const slide = slides[current];

  return (
    <section
      className="hc-wrap"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className={`hc-slide ${
          transitioning ? `hc-slide--exit-${animDir}` : 'hc-slide--enter'
        }`}
        key={slide.id}
      >
        {/* ✅ <picture> tag — browser picks correct image automatically */}
        <picture>
          <source media="(max-width: 640px)" srcSet={slide.mobile} />
          <source media="(min-width: 641px)" srcSet={slide.desktop} />
          <img
            src={slide.desktop}
            alt={slide.alt}
            className="hc-img"
            draggable={false}
          />
        </picture>
      </div>

      <button className="hc-arrow hc-arrow--prev" onClick={handlePrev} aria-label="Previous slide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <button className="hc-arrow hc-arrow--next" onClick={handleNext} aria-label="Next slide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      <div className="hc-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`hc-dot ${i === current ? 'hc-dot--active' : ''}`}
            onClick={() => handleDot(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroCarousel;